import React, { Component, createRef } from 'react';

import { events } from '../constants';
import { login } from '../../../api/login';
import { getSearchParam } from './helpers/url';
import { passwordCheck } from '../../create-account-panel/utils/validations';
import { createAccount } from '../../../api/createAccount';

class PasswordInput extends Component {
  constructor(props) {
    super(props);
    this.iframeId = null;
    this.targetOrigin = null;
    this.inputPasswordRef = createRef();
    this.inputUsernameRef = createRef();
    this.formRef = createRef();
    this.submitButtonRef = createRef();
    this.state = {
      isShow: false,
      active: false,
      password: '',
      username: '',
    };
  }

  componentDidMount() {
    this.iframeId = getSearchParam('id');
    this.targetOrigin = getSearchParam('targetOrigin');
    window.addEventListener('message', this.receiveMessage, false);
    this.postMessage({ action: events.READY });
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.receiveMessage, false);
  }

  handleHandshakeRequest = event => {
    const { id } = event?.data || {};

    this.postMessage({
      id,
      action: events.HANDSHAKE.RESPONSE,
      data: 'success',
    });
  };

  handleLoginRequest = async event => {
    const { password } = this.state;
    const { payload, id } = event?.data || {};
    const {
      email,
      globals = { realm: '', plid: '', host: '', url_params: {} },
      username,
      remember_me,
      link_account,
      login_method,
    } = payload;
    const loginArgs = { email, globals, username, password, remember_me, link_account, login_method };

    try {
      await this.setUsername(username);
      const response = await login(loginArgs);
      this.submitButtonRef.current.click();
      this.postMessage({
        id,
        action: events.LOGIN.SUCCESS,
        data: {
          data: response.data,
        },
      });
    } catch (error) {
      this.postMessage({
        id,
        action: events.LOGIN.FAILURE,
        error: {
          data: error.data,
        },
      });
    }
  };

  handleSetFocus = event => {
    const { payload, id } = event?.data || {};
    const isFocused = document.activeElement === this.inputPasswordRef.current;

    if (payload && !isFocused) {
      this.inputPasswordRef.current?.focus();
    } else if (!payload && isFocused) {
      this.inputPasswordRef.current?.blur();
    }

    this.postMessage({ id, data: 'done' });
  };

  handleShowPassword = event => {
    const { payload, id } = event?.data || {};
    this.postMessage({ id, data: 'done' });
    this.setState(state => ({ ...state, isShow: payload }));
  };

  handleCreateAccountRequest = async event => {
    const { password } = this.state;
    const { payload, id } = event?.data || {};
    const {
      email,
      globals = { host: '', version: '', url_params: {}, plid: '' },
      username,
      last_name,
      session_id,
      first_name,
      mobile_phone,
      consent_preferences,
    } = payload;
    const createAccountArgs = {
      email,
      globals,
      username,
      session_id,
      consent_preferences,
      password,
      first_name,
      last_name,
      mobile_phone,
    };

    try {
      await this.setUsername(username);
      const response = await createAccount(createAccountArgs);
      this.submitButtonRef.current.click();
      this.postMessage({
        id,
        action: events.CREATE_ACCOUNT.SUCCESS,
        data: {
          data: response.data,
        },
      });
    } catch (error) {
      this.postMessage({
        id,
        action: events.CREATE_ACCOUNT.FAILURE,
        error: {
          data: error.data,
        },
      });
    }
  };

  handleError = (event, error) => {
    const { id } = event?.data || {};
    this.postMessage({
      id,
      action: events.ERROR,
      error: {
        data: error,
      },
    });
  };

  handleValidateRequest = event => {
    const { password } = this.state;
    const { id } = event?.data || {};

    const error = passwordCheck(password);

    this.postMessage({
      id,
      action: events.VALIDATE.RESPONSE,
      data: {
        valid: !error,
        error,
      },
    });
  };

  receiveMessage = event => {
    const { action, iframeId } = event.data || {};

    if (iframeId !== this.iframeId) {
      return;
    }

    try {
      switch (action) {
        case events.SET_FOCUS: {
          this.handleSetFocus(event);
          break;
        }
        case events.LOGIN.REQUEST: {
          this.handleLoginRequest(event);
          break;
        }
        case events.SHOW_PASSWORD: {
          this.handleShowPassword(event);
          break;
        }
        case events.CREATE_ACCOUNT.REQUEST: {
          this.handleCreateAccountRequest(event);
          break;
        }
        case events.VALIDATE.REQUEST: {
          this.handleValidateRequest(event);
          break;
        }
        case events.HANDSHAKE.REQUEST: {
          this.handleHandshakeRequest(event);
          break;
        }
        default: {
          this.handleError(event, `unknown_action: ${action}`);
        }
      }
    } catch (e) {
      this.handleError(event, e);
    }
  };

  postMessage = ({ id = '', action = '', data = null, error = null, iframeId = null }) => {
    const message = { id, action, data, error, iframeId: iframeId ? iframeId : this.iframeId };
    window.parent?.postMessage(message, this.targetOrigin);
  };

  onKeyPress = e => {
    e.persist();

    this.postMessage({
      action: events.INPUT.KEY_PRESS,
      data: {
        which: e.which,
        target: { id: e.target.id },
        CapsLock: e.getModifierState && e.getModifierState('CapsLock'),
      },
    });
  };

  onChange = e => {
    e.persist();
    const { active } = this.state;
    const { value: password } = e.target;
    const newActive = !!password.length;

    if (newActive !== active) {
      this.postMessage({ action: events.INPUT.ACTIVE, data: newActive });
    }

    this.postMessage({ action: events.INPUT.CHANGE });

    this.setState(state => ({
      ...state,
      active: newActive,
      password: e.target.value,
    }));
  };

  onBlur = () => {
    this.postMessage({ action: events.INPUT.BLUR });
  };

  onFocus = () => {
    if (!this.state.active) {
      this.postMessage({ action: events.INPUT.FOCUS });
    }
  };

  onAutofill = field => value => {
    const data = { field, value };
    this.postMessage({ action: events.AUTOFILL, data });
  };

  setUsername = username => this.setState(state => ({ ...state, username }));

  render() {
    const { isShow, password, username } = this.state;
    return (
      <form
        id='password-container'
        data-testid='password-container'
        ref={this.formRef}
        onSubmit={e => {
          e.preventDefault();
        }}
      >
        <input
          id='username'
          aria-label='username'
          ref={this.inputUsernameRef}
          name='username'
          type='text'
          onChange={e => {
            e.persist();
            const { value } = e.target;
            this.onAutofill('username')(value);
            this.setUsername('');
          }}
          tabIndex={-1}
          value={username}
        />
        <input
          autoComplete='new-password'
          id='password'
          aria-label='password'
          ref={this.inputPasswordRef}
          type={isShow ? 'text' : 'password'}
          value={password}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          onChange={this.onChange}
          onKeyPress={this.onKeyPress}
        />
        <button id='submit-button' type='submit' aria-label='submit' ref={this.submitButtonRef} />
      </form>
    );
  }
}

export default PasswordInput;
